import { z } from 'zod';
/**
 * Public Experiences — shared primitive schemas (identifiers, timestamps,
 * revisions, provider identifiers).
 *
 * Keeping these in one place guarantees the same validation on the public API,
 * the admin API and the persisted Firestore documents.
 */
/**
 * All timestamps crossing an API boundary **and** all timestamps inside the
 * persisted document schemas are ISO-8601 strings. The backend converts
 * Firestore `Timestamp` values to/from ISO-8601 at the repository boundary, so
 * no Firestore type ever reaches this package or a browser.
 *
 * Offsets are accepted for tolerance; the backend always emits UTC (`...Z`).
 */
export const IsoTimestampSchema = z.string().datetime({ offset: true });
/**
 * Public, shareable experience identifier.
 *
 * It is the FIRST path segment of the public page URL
 * (`{POINTPAY_PUBLIC_BASE_URL}/{publicId}`), so it shares a namespace with the
 * pointpay-public router's own routes. Generation rules, chosen so a generated
 * id can never collide with an existing or future static route:
 *
 *  - exactly `PUBLIC_ID_LENGTH` (10) characters;
 *  - lowercase base32 / alphanumeric only (`[a-z0-9]`) — no uppercase, no
 *    underscore, no hyphen, no dot, so it is case-insensitively unambiguous in a
 *    URL and cannot look like a hyphenated route such as `payment-link`;
 *  - generators SHOULD draw from Crockford base32 lowercase
 *    (`PUBLIC_ID_GENERATION_ALPHABET`, which omits `i`, `l`, `o` and `u`) to
 *    avoid visually confusable characters when a customer types the link;
 *    validation accepts the wider `[a-z0-9]` set so already-issued ids keep
 *    parsing if the alphabet is ever changed.
 *
 * 10 characters of that alphabet is ~51 bits (~1.1e15 values), so it is
 * unguessable in practice — but it is still only an ADDRESSING token, never an
 * authorization token. Reading a private checkout always requires the guest
 * capability (see `CheckoutCapabilitySchema`).
 *
 * Generation is a **CSPRNG** draw over `PUBLIC_ID_GENERATION_ALPHABET`, claimed
 * by creating `/publicExperienceReferences/{publicId}` inside a Firestore
 * transaction, so a collision retries instead of overwriting another business's
 * reference.
 */
export const PUBLIC_ID_LENGTH = 10;
export const PUBLIC_ID_REGEX = /^[a-z0-9]{10}$/;
/** Crockford base32 lowercase (no i/l/o/u). Generation alphabet, not validation. */
export const PUBLIC_ID_GENERATION_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';
/**
 * First-path-segment names owned by the pointpay-public site (or reserved for
 * it). A generated `publicId` must never equal one of these, or the public page
 * would shadow a real route.
 *
 * The length-10 lowercase-alphanumeric rule above already excludes every entry
 * below, so this list is defence in depth: it keeps the guarantee correct if the
 * length rule is ever relaxed, and it documents the namespace the id lives in.
 * Extend it whenever a new top-level pointpay-public route is added.
 */
export const RESERVED_PUBLIC_ID_SEGMENTS = [
    'register',
    'donate',
    'b',
    'receipt',
    'payment-link',
    'secure-upload',
    'sec-file-upload',
    '404',
    'system',
    'public',
    'x',
    'integrations',
];
/** Case-insensitive reserved-segment check. */
export const isReservedPublicIdSegment = (value) => RESERVED_PUBLIC_ID_SEGMENTS.includes(value.trim().toLowerCase());
export const PublicExperiencePublicIdSchema = z
    .string()
    .trim()
    .regex(PUBLIC_ID_REGEX, `publicId must be exactly ${PUBLIC_ID_LENGTH} lowercase alphanumeric characters`)
    .refine((value) => !isReservedPublicIdSegment(value), {
    message: 'publicId must not be a reserved pointpay-public route segment',
});
/**
 * Backwards-compatible alias for the regex name used while drafting. Kept so
 * consumers have one obvious import; both point at the same pattern.
 */
export const PUBLIC_EXPERIENCE_PUBLIC_ID_REGEX = PUBLIC_ID_REGEX;
/**
 * Server-generated Firestore document id (business, experience, submission).
 * Restricted to URL-safe characters so it can be used in a path segment.
 */
export const FirestoreDocIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/, 'must be a URL-safe document id');
export const BusinessIdSchema = FirestoreDocIdSchema;
export const ExperienceIdSchema = FirestoreDocIdSchema;
export const SubmissionIdSchema = FirestoreDocIdSchema;
/**
 * Checkout identity. The client supplies `attemptKey` (a UUID it generates once
 * per checkout attempt and keeps in `sessionStorage`); the server uses it
 * verbatim as the `checkoutId`, so one attempt can never create two checkouts.
 */
export const AttemptKeySchema = z.string().trim().uuid();
export const CheckoutIdSchema = z.string().trim().uuid();
/**
 * Monotonic revision counters. `draftRevision` increments on every saved draft
 * edit; `publishedRevision` increments on every publish.
 */
export const RevisionSchema = z.number().int().min(0);
export const PublishedRevisionSchema = z.number().int().min(1);
/** SHA-256 hex digest (request hash, capability hash). Never the secret itself. */
export const Sha256HexSchema = z
    .string()
    .regex(/^[a-f0-9]{64}$/, 'must be a lowercase sha256 hex digest');
/**
 * Guest capability secret: 32 random bytes from a **CSPRNG**, base64url-encoded
 * (43 chars, no padding). Returned exactly once per checkout create/replay, sent
 * back in the `X-Checkout-Capability` header, and compared server-side against
 * the stored sha256 digest with `timingSafeEqual`.
 *
 * Hash-only storage: the raw secret is never persisted and **never logged** — it
 * must appear on the backend redaction lists (BE-08).
 */
export const CHECKOUT_CAPABILITY_REGEX = /^[A-Za-z0-9_-]{43}$/;
export const CheckoutCapabilitySchema = z
    .string()
    .regex(CHECKOUT_CAPABILITY_REGEX, 'capability must be 32 bytes base64url-encoded (43 chars)');
/**
 * Primary key of the `All_Payment_Accounts` / `Stripe_Accounts` row that routes
 * this offer. Sequelize declares these as UUID primary keys, so it is a UUID
 * **string**, never a numeric id.
 *
 * The package has no shared uuid helper (checked: every other module writes
 * `z.string().uuid()` inline), so this follows that convention and is named once
 * here because two schemas use it.
 */
export const ProviderAccountDbIdSchema = z.string().uuid();
/** Provider object identifiers. Private/admin surfaces only — never public. */
export const StripeAccountIdSchema = z
    .string()
    .regex(/^acct_[A-Za-z0-9]+$/, 'must be a Stripe account id');
export const StripeProductIdSchema = z
    .string()
    .regex(/^prod_[A-Za-z0-9]+$/, 'must be a Stripe product id');
export const StripePriceIdSchema = z
    .string()
    .regex(/^price_[A-Za-z0-9]+$/, 'must be a Stripe price id');
export const StripeCheckoutSessionIdSchema = z
    .string()
    .regex(/^cs_[A-Za-z0-9_]+$/, 'must be a Stripe Checkout Session id');
export const StripeSubscriptionIdSchema = z
    .string()
    .regex(/^sub_[A-Za-z0-9]+$/, 'must be a Stripe subscription id');
export const StripeCustomerIdSchema = z
    .string()
    .regex(/^cus_[A-Za-z0-9]+$/, 'must be a Stripe customer id');
export const StripeInvoiceIdSchema = z
    .string()
    .regex(/^in_[A-Za-z0-9]+$/, 'must be a Stripe invoice id');
export const StripePaymentIntentIdSchema = z
    .string()
    .regex(/^pi_[A-Za-z0-9]+$/, 'must be a Stripe payment intent id');
/**
 * Publishable (client) key. Safe for the browser by design; it is not a secret
 * API key and a secret key (`sk_...`/`rk_...`) must never be accepted here.
 */
export const StripePublishableKeySchema = z
    .string()
    .regex(/^pk_(test|live)_[A-Za-z0-9]+$/, 'must be a Stripe publishable key');
/**
 * Opaque, session-scoped Checkout Session client secret used by Stripe Embedded
 * Checkout. Issued only to the authorized guest context, never written to the
 * public Firestore projection, never logged.
 */
export const StripeCheckoutClientSecretSchema = z.string().min(10).max(2048);
/** Provider idempotency key recorded on the checkout document. */
export const ProviderIdempotencyKeySchema = z.string().min(8).max(255);
/** Absolute https URL (branding assets, return URL). */
export const HttpsUrlSchema = z
    .string()
    .trim()
    .max(2048)
    .url()
    .refine((value) => value.startsWith('https://'), {
    message: 'must be an https URL',
});
/** `#RRGGBB` colour used by the minimal branding block. */
export const HexColorSchema = z
    .string()
    .trim()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'must be a #RRGGBB hex colour');
